// src/pages/IncomeDistributions.js
import React from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Stack,
  Button,
  Chip,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { useNavigate } from "react-router-dom"
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import PaidIcon from "@mui/icons-material/Paid";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PieChartOutlineIcon from "@mui/icons-material/PieChartOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import BarChartIcon from "@mui/icons-material/BarChart";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

const StatCard = ({ icon, title, value, subtitle }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: 2,
      border: "1px solid #e5e7eb",
      boxShadow: "0px 16px 40px rgba(15,23,42,0.06)",
      bgcolor: "#ffffff",
      height: "100%",
    }}
  >
    <Stack spacing={2}>
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "12px",
          bgcolor: "background.subtle",
          border: (theme) => `1px solid ${theme.palette.border.subtle}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>

      <Box>
        <Typography variant="body2" sx={{ color: "#6b7280", mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#111827" }}>
          {value}
        </Typography>
        <Typography variant="caption" sx={{ color: "#6b7280" }}>
          {subtitle}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const monthlyIncomeData6M = [
  { month: "May", value: 0 },
  { month: "Jun", value: 1900 },
  { month: "Jul", value: 3000 },
  { month: "Aug", value: 3100 },
  { month: "Sep", value: 3100 },
  { month: "Oct", value: 3300 },
  { month: "Nov", value: 3200 },
];

// simple 12M example – you can tweak values as needed
const monthlyIncomeData12M = [
  { month: "Dec", value: 1500 },
  { month: "Jan", value: 1700 },
  { month: "Feb", value: 1800 },
  { month: "Mar", value: 2000 },
  { month: "Apr", value: 2100 },
  ...monthlyIncomeData6M,
];

const payoutHistory = [
  {
    asset: "MENA Tech Growth Fund",
    category: "Financial Products",
    type: "Dividend",
    period: "Q3 2025",
    tokens: 80,
    amount: "AED 1,600",
    date: "1 Nov 2025",
    status: "Completed",
  },
  {
    asset: "Metropolitan Tower",
    category: "Real Estate",
    type: "Rental Income",
    period: "October 2025",
    tokens: 1250,
    amount: "AED 2,150",
    date: "1 Nov 2025",
    status: "Completed",
  },
  {
    asset: "Mohammed bin Rashid Solar Park",
    category: "Renewable Energy",
    type: "Revenue Share",
    period: "October 2025",
    tokens: 20,
    amount: "AED 1,950",
    date: "20 Oct 2025",
    status: "Completed",
  },
  {
    asset: "DMCC Gold Vault - Premium",
    category: "Commodities",
    type: "Storage Fee Rebate",
    period: "Q3 2025",
    tokens: 375,
    amount: "AED 890",
    date: "15 Oct 2025",
    status: "Completed",
  },
];

const upcomingPayouts = [
  {
    asset: "MENA Tech Growth Fund",
    category: "Financial Products",
    type: "Dividend",
    period: "Q4 2025",
    tokens: 80,
    amount: "AED 1,650",
    date: "15 Dec 2025",
    status: "Estimated",
  },
  {
    asset: "Metropolitan Tower",
    category: "Real Estate",
    type: "Rental Income",
    period: "November 2025",
    tokens: 1250,
    amount: "AED 2,150",
    date: "1 Dec 2025",
    status: "Estimated",
  },
  {
    asset: "Mohammed bin Rashid Solar Park",
    category: "Renewable Energy",
    type: "Revenue Share",
    period: "November 2025",
    tokens: 20,
    amount: "AED 1,950",
    date: "20 Nov 2025",
    status: "Estimated",
  },
];

const categories = [
  "All Categories",
  "Real Estate",
  "Commodities",
  "Debt",
  "Islamic Finance",
  "Arts",
  "Luxury",
  "Supply Chain",
  "Asset-Backed",
];

const IncomeDistributions = () => {
  const navigate = useNavigate()
  const [activeCategory, setActiveCategory] = React.useState("All Categories");
  const [trendTab, setTrendTab] = React.useState(0);

  const statCards = [
    {
      key: "total-income",
      icon: (
        <TrendingUpIcon
          sx={{ color: "text.icon" }}
        />
      ),
      title: "Total Income Earned",
      value: "AED 19,290",
      subtitle: "From 14 payouts",
    },
    {
      key: "this-month",
      icon: (
        <CalendarTodayIcon
          sx={{ color: "text.icon" }}
        />
      ),
      title: "This Month",
      value: "AED 3,240",
      subtitle: "Avg: AED 3,215/month",
    },
    {
      key: "next-payout",
      icon: (
        <AccessTimeIcon
          sx={{ color: "text.icon" }}
        />
      ),
      title: "Next Payout",
      value: "AED 14,330",
      subtitle: "December 1, 2025",
    },
    {
      key: "active-streams",
      icon: (
        <PieChartOutlineIcon
          sx={{ color: "text.icon" }}
        />
      ),
      title: "Active Income Streams",
      value: "11",
      subtitle: "Across 10 categories",
    },
  ];

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 2,
        display: "flex",
      }}
    >
      <Box sx={{ width: "100%", minWidth: 0 }}>
        {/* Back link */}
        <Button
          onClick={() => navigate("/asset/dashboard")}
          startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
          sx={{
            textTransform: "none",
            color: "#111827",
            mb: 2,
            px: 0,
            "&:hover": { backgroundColor: "transparent" },
          }}
        >
          Back to Dashboard
        </Button>

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: 1,
                bgcolor: "background.headerIcon",
                border: (theme) => `1px solid ${theme.palette.border.light}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PaidIcon sx={{ color: "text.slate" }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: "#111827" }}
              >
                Income &amp; Distributions
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                Track earnings across all tokenized assets
              </Typography>
            </Box>
          </Stack>

          <Chip
            icon={<CheckCircleOutlineIcon />}
            label="All verified"
            sx={{
              bgcolor: "#ecfdf3",
              color: "#16a34a",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              "& .MuiChip-icon": {
                color: "#16a34a",
              },
            }}
          />
        </Box>

        {/* Stats row */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {statCards.map((card) => (
            <Grid size={{ xs: 12, md: 3 }} key={card.key}>
              <StatCard
                icon={card.icon}
                title={card.title}
                value={card.value}
                subtitle={card.subtitle}
              />
            </Grid>
          ))}
        </Grid>

        {/* Filter by Category */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: "1px solid #e5e7eb",
            bgcolor: "#ffffff",
            mb: 3,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <FilterListIcon sx={{ fontSize: 18, color: "#6b7280" }} />
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "#111827" }}
            >
              Filter by Category
            </Typography>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
          </Stack>

          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            {categories.map((c) => {
              const isActive = c === activeCategory;
              return (
                <Button
                  key={c}
                  onClick={() => setActiveCategory(c)}
                  variant={isActive ? "contained" : "text"}
                  sx={{
                    textTransform: "none",
                    borderRadius: 999,
                    fontSize: 13,
                    px: 2.5,
                    py: 0.75,
                    minHeight: 0,
                    ...(isActive
                      ? {
                        bgcolor: "#0284c7",
                        "&:hover": { bgcolor: "#0369a1" },
                      }
                      : {
                        color: "#4b5563",
                        "&:hover": { bgcolor: "#f3f4f6" },
                      }),
                  }}
                >
                  {c}
                </Button>
              );
            })}
          </Stack>
        </Paper>

        {/* How income distribution works info banner */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: "1px solid #bae6fd",
            bgcolor: "#e0f2fe",
            mb: 3,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                bgcolor: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 20, color: "#0284c7" }} />
            </Box>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#0f172a", mb: 0.5 }}
              >
                How Income Distribution Works
              </Typography>
              <Typography variant="body2" sx={{ color: "#0f172a" }}>
                Payouts are distributed proportionally based on your token
                holdings. Income types include rental income (real estate),
                dividends (financial products), revenue shares (energy &amp;
                arts), and storage rebates (commodities). All payments are
                secured through blockchain.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Monthly Income Trend */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: "1px solid #e5e7eb",
            bgcolor: "#ffffff",
            mb: 3,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <BarChartIcon sx={{ fontSize: 18, color: "#0ea5e9" }} />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#111827" }}
                >
                  Monthly Income Trend
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: "#6b7280" }}>
                Income performance over the last 6 months
              </Typography>
            </Box>

            <Tabs
              value={trendTab}
              onChange={(_, v) => setTrendTab(v)}
              sx={{
                minHeight: 0,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontSize: 12,
                  minHeight: 0,
                  px: 1.5,
                },
              }}
            >
              <Tab label="6M" />
              <Tab label="12M" />
            </Tabs>
          </Stack>

          <Box sx={{ mt: 1, height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={
                  trendTab === 0 ? monthlyIncomeData6M : monthlyIncomeData12M
                }
                margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e5e7eb"
                  vertical={false}
                />
                <XAxis
                  dataKey="month"
                  tickLine={false}
                  axisLine={{ stroke: "#e5e7eb" }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={{ stroke: "#e5e7eb" }}
                  tickFormatter={(v) => `${Math.round(v / 900) * 0.9}k`}
                />
                <Tooltip formatter={(v) => [`$${v}`, "Income"]} />
                <Bar
                  dataKey="value"
                  radius={[6, 6, 0, 0]}
                  barSize={42}
                  fill="#00C4CC" // teal like your Figma
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        {/* Payout History */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: "1px solid #e5e7eb",
            bgcolor: "#ffffff",
            mb: 3,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 18, color: "#22c55e" }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#111827" }}
                >
                  Payout History
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: "#6b7280" }}>
                Completed income distributions
              </Typography>
            </Box>

            <Button
              variant="outlined"
              size="small"
              startIcon={<CloudDownloadOutlinedIcon />}
              sx={{
                textTransform: "none",
                borderRadius: 999,
                fontSize: 12,
              }}
            >
              Export
            </Button>
          </Stack>

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Asset</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Tokens</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payoutHistory.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "#111827" }}
                      >
                        {row.asset}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#6b7280" }}>
                        {row.category}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.type}
                        size="small"
                        sx={{
                          borderRadius: 999,
                          bgcolor: "#f3f4f6",
                          fontSize: 11,
                        }}
                      />
                    </TableCell>
                    <TableCell>{row.period}</TableCell>
                    <TableCell>{row.tokens}</TableCell>
                    <TableCell>{row.amount}</TableCell>
                    <TableCell>{row.date}</TableCell>
                    <TableCell>
                      <Chip
                        icon={<CheckCircleOutlineIcon />}
                        label="Completed"
                        size="small"
                        sx={{
                          borderRadius: 999,
                          bgcolor: "#ecfdf3",
                          color: "#16a34a",
                          fontSize: 11,
                          "& .MuiChip-icon": { color: "#16a34a" },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>

        {/* Upcoming Payouts */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: "1px solid #e5e7eb",
            bgcolor: "#ffffff",
            mb: 3,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#111827", mb: 0.5 }}
              >
                Upcoming Payouts
              </Typography>
              <Typography variant="caption" sx={{ color: "#6b7280" }}>
                Projected income distributions
              </Typography>
            </Box>

            <Button
              variant="outlined"
              size="small"
              sx={{
                textTransform: "none",
                borderRadius: 999,
                fontSize: 12,
              }}
            >
              Estimates
            </Button>
          </Stack>

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Asset</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Tokens</TableCell>
                  <TableCell>Est. Amount</TableCell>
                  <TableCell>Expected Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {upcomingPayouts.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "#111827" }}
                      >
                        {row.asset}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#6b7280" }}>
                        {row.category}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.type}
                        size="small"
                        sx={{
                          borderRadius: 999,
                          bgcolor: "#f3f4f6",
                          fontSize: 11,
                        }}
                      />
                    </TableCell>
                    <TableCell>{row.period}</TableCell>
                    <TableCell>{row.tokens}</TableCell>
                    <TableCell>{row.amount}</TableCell>
                    <TableCell>{row.date}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default IncomeDistributions;
